<template>
    <div class="venue-selector">
        <!-- EXPANDED SIDEBAR: Venue item with label + sublabel + dropdown button -->
        <div
            v-if="sidebarOpen"
            class="venue-nav-item flex align-items-center p-3 hover:bg-sidebar-hover text-bluegray-100 hover:text-bluegray-50 transition-duration-150 transition-colors"
            :class="{ 'bg-sidebar-active': isHomeRoute }">
            <!-- Clickable area for navigation -->
            <router-link
                v-ripple
                :to="homeLink"
                class="venue-link flex align-items-center flex-1 min-w-0 cursor-pointer p-ripple no-underline text-bluegray-100 hover:text-bluegray-50">
                <i
                    class="far fa-map text-center flex-shrink-0"
                    style="width: 2em"></i>
                <div class="ml-2 flex-1 min-w-0">
                    <div class="font-medium">Venue</div>
                    <div
                        class="text-xs text-bluegray-400 text-overflow-ellipsis overflow-hidden white-space-nowrap">
                        {{
                            !dataReady
                                ? "Loading..."
                                : currentVenue?.name || "No venues"
                        }}
                    </div>
                </div>
            </router-link>

            <!-- Dropdown button (expanded) -->
            <button
                v-if="hasMultipleVenues"
                v-tooltip.top="'Switch venue'"
                @click.stop="toggleDropdown"
                class="venue-dropdown-btn flex align-items-center justify-content-center cursor-pointer border-none bg-transparent text-bluegray-300 hover:text-bluegray-50 hover:bg-white-alpha-10 border-round transition-duration-150 ml-2">
                <i class="far fa-chevron-down"></i>
            </button>
        </div>

        <!-- COLLAPSED SIDEBAR: Icon navigates to map, long-press or right-click for dropdown -->
        <div v-else class="venue-collapsed-wrapper">
            <router-link
                v-ripple
                v-tooltip.right="{
                    value: currentVenue?.name || 'Venue',
                    showDelay: 300,
                }"
                :to="homeLink"
                class="venue-nav-item-collapsed flex align-items-center justify-content-center cursor-pointer p-3 hover:bg-sidebar-hover text-bluegray-100 hover:text-bluegray-50 transition-duration-150 transition-colors p-ripple no-underline"
                :class="{ 'bg-sidebar-active': isHomeRoute }">
                <i class="far fa-map"></i>
            </router-link>
            <!-- Small dropdown indicator for collapsed mode -->
            <button
                v-if="hasMultipleVenues"
                v-tooltip.right="'Switch venue'"
                @click.stop="toggleDropdown"
                class="venue-collapsed-dropdown">
                <i class="far fa-chevron-down"></i>
            </button>
        </div>

        <!-- Dropdown Menu -->
        <Menu
            ref="menuRef"
            :model="venueMenuItems"
            :popup="true"
            class="venue-menu">
            <template #item="{ item, label, props: itemProps }">
                <a v-ripple v-bind="itemProps.action">
                    <span v-bind="itemProps.icon" />
                    <span v-bind="itemProps.label">{{ label }}</span>
                    <Badge
                        v-if="item.incidentCount > 0"
                        :value="item.incidentCount"
                        v-tooltip.right="'Active incidents'"
                        class="venue-incident-badge" />
                </a>
            </template>
        </Menu>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import Menu from "primevue/menu";
import Badge from "primevue/badge";
import { useViewContextStore } from "@/stores/viewContext";
import { useStaticDataStore } from "@/stores/staticData";
import { useVenueDataStore } from "@/stores/venueData";
import { storeToRefs } from "pinia";
import type { Venue } from "@/types/api";

interface Props {
    sidebarOpen?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    sidebarOpen: true,
});

const router = useRouter();
const route = useRoute();
const viewContext = useViewContextStore();
const staticDataStore = useStaticDataStore();
const venueDataStore = useVenueDataStore();

const { venueId, homeLink, homeRouteName } = storeToRefs(viewContext);
const { getVenues, dataReady } = storeToRefs(staticDataStore);

// Check if currently on a home route
const isHomeRoute = computed(
    () => route.name === "operator-home" || route.name === "installer-home",
);

const menuRef = ref();

// Venues from EdgeNode (staticDataStore)
const venues = computed<Venue[]>(() => getVenues.value || []);

// Check if multiple venues exist
const hasMultipleVenues = computed(() => venues.value.length > 1);

// Current selected venue
const currentVenue = computed(() => {
    if (!venueId.value || !venues.value.length) return null;
    return venues.value.find((v) => v.id === venueId.value) || null;
});

// Build menu items for venue dropdown
const venueMenuItems = computed(() => {
    const items: any[] = [
        {
            label: "Switch Venue",
            items: venues.value.map((venue) => {
                const isSelected = venue.id === venueId.value;
                return {
                    label: venue.name,
                    icon: isSelected ? "fas fa-check" : "far fa-map",
                    class: isSelected ? "venue-selected" : "",
                    // Live via the stream; other venues fill in once the background load finishes.
                    incidentCount: venueDataStore.getActiveIncidents(venue.id)
                        .length,
                    command: () => selectVenue(venue.id),
                };
            }),
        },
    ];
    return items;
});

const toggleDropdown = (event: Event) => {
    menuRef.value?.toggle(event);
};

const selectVenue = (id: number) => {
    menuRef.value?.hide();

    // Navigate to the selected venue.
    // AuthenticatedLayout's route watcher detects the venueId change and calls
    // setupVenueContext, which fetches users, level data, and sets the correct
    // venue/level context. Pre-setting viewContext here would make the route
    // sync skip setupVenueContext, so users and level data never load.
    router.push({
        name: homeRouteName.value,
        params: { venueId: id },
    });
};
</script>

<style scoped>
.venue-selector {
    width: 100%;
    position: relative;
}

.venue-nav-item {
    width: 100%;
}

.venue-collapsed-wrapper {
    position: relative;
    width: 100%;
}

.venue-nav-item-collapsed {
    width: 100%;
}

.venue-collapsed-dropdown {
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.4);
    border: none;
    border-radius: 3px;
    color: var(--bluegray-300);
    cursor: pointer;
    font-size: 8px;
    transition: all 0.15s ease;
}

.venue-collapsed-dropdown:hover {
    background: rgba(0, 0, 0, 0.6);
    color: var(--bluegray-100);
}

.venue-link {
    flex: 1;
    min-width: 0;
}

.venue-dropdown-btn {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
}
</style>

<!-- Global styles for the popup menu (rendered outside component scope) -->
<style>
.venue-menu.p-menu {
    min-width: 180px;
    padding: 0.25rem;
}

.venue-menu .p-menuitem-link,
.venue-menu .p-menuitem-content {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
}

.venue-menu .p-menuitem-icon {
    font-size: 0.75rem;
    margin-right: 0.5rem;
}

.venue-menu .p-submenu-header {
    font-weight: 600;
    font-size: 0.625rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-color-secondary);
    padding: 0.5rem 0.75rem 0.25rem;
}

/* Outlined count, as in GroupsList's filter badges, ringed in the text colour. */
.venue-menu .venue-incident-badge.p-badge {
    margin-left: auto;
    /* Overhang the label's line box so the row keeps its height. */
    margin-block: -0.25rem;
    flex-shrink: 0;
    padding: 0 0.25rem;
    background: transparent;
    color: inherit;
    border: 1px solid var(--text-color-secondary);
    min-width: 1.25rem;
    height: 1.25rem;
    line-height: calc(1.25rem - 2px);
    font-size: 0.7rem;
    font-weight: 600;
}

.venue-menu .p-menuitem-text {
    margin-right: 0.75rem;
}

.venue-menu .venue-selected .venue-incident-badge.p-badge {
    border-color: rgba(255, 255, 255, 0.6);
}

/* Selected venue styling */
.venue-menu .p-menuitem.venue-selected,
.venue-menu li.venue-selected {
    background: var(--primary-color) !important;
    border-radius: 4px;
}

.venue-menu .p-menuitem.venue-selected .p-menuitem-link,
.venue-menu .p-menuitem.venue-selected .p-menuitem-content,
.venue-menu li.venue-selected .p-menuitem-link,
.venue-menu li.venue-selected .p-menuitem-content {
    background: transparent !important;
    color: white !important;
    font-weight: 600;
}

.venue-menu .p-menuitem.venue-selected .p-menuitem-icon,
.venue-menu li.venue-selected .p-menuitem-icon,
.venue-menu .p-menuitem.venue-selected .p-menuitem-text,
.venue-menu li.venue-selected .p-menuitem-text {
    color: white !important;
}
</style>
